var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { jsx as _jsx } from "react/jsx-runtime";
import { useCallback, useMemo } from 'react';
import { Animated, Platform } from 'react-native';
import { Configuration } from '../../../Domain/DTOs/Configuration/ConfigurationDTO';
import { component } from '../../Decorators/Component';
import { CoreConfig } from './../../../CoreConfig';
import { useColorScheme } from '../../Hooks/UseColorScheme';
import { getTextColor } from '../../../Helpers/Hex/GetComponentColor';
import { useColorSchemeContext } from '../../Contexts/ThemeContext';
import { useDirection } from '../../Hooks/UseDirection';
function getFontWeight(style, text) {
    if (!style) {
        return 'regular';
    }
    if (Array.isArray(style)) {
        const styleObj = [...style].reverse().find(x => {
            if (!x || typeof x !== 'object') {
                return false;
            }
            if (Array.isArray(x)) {
                return getFontWeight(x);
            }
            return x.fontWeight;
        });
        return getFontWeight(styleObj, text);
    }
    if (!style.fontWeight) {
        return 'regular';
    }
    const fontWeight = style.fontWeight;
    const weights = {
        '300': 'light',
        '600': 'semibold',
        '700': 'bold',
    };
    if (isNaN(Number(fontWeight))) {
        return fontWeight;
    }
    return weights[fontWeight];
}
function findTextAlign(style) {
    if (!style || typeof style !== 'object') {
        return undefined;
    }
    if (Array.isArray(style)) {
        for (let i = style.length - 1; i > -1; i--) {
            const s = style[i];
            const textAlign = findTextAlign(s);
            if (textAlign) {
                return textAlign;
            }
        }
        return undefined;
    }
    return style.textAlign;
}
function getTextAlign(dir, style) {
    if (dir !== 'rtl') {
        return undefined;
    }
    if (Platform.OS !== 'web') {
        if (!style) {
            return 'left';
        }
        return findTextAlign(style) || 'left';
    }
    if (!style) {
        return 'right';
    }
    const textAlign = findTextAlign(style);
    if (!textAlign || textAlign === 'left') {
        return 'right';
    }
    if (textAlign === 'right') {
        return 'left';
    }
    return undefined;
}
export const AnimatedTextView = component({
    provider: CoreConfig.provider,
    moduleName: CoreConfig.moduleName,
    componentName: Configuration.ComponentNames.animatedText,
    style(theme, mediaQuery, window, props) {
        var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p, _q, _r;
        return {
            container: {
                fontFamily: (_a = theme.fonts) === null || _a === void 0 ? void 0 : _a.primary,
                color: (_b = theme.colors) === null || _b === void 0 ? void 0 : _b.text_color
            },
            light: {
                fontFamily: ((_c = theme.fonts) === null || _c === void 0 ? void 0 : _c.light) || ((_d = theme.fonts) === null || _d === void 0 ? void 0 : _d.primary),
                fontWeight: '300'
            },
            semibold: {
                fontFamily: ((_e = theme.fonts) === null || _e === void 0 ? void 0 : _e.semibold) || ((_f = theme.fonts) === null || _f === void 0 ? void 0 : _f.primary),
                fontWeight: '600'
            },
            bold: {
                fontFamily: ((_g = theme.fonts) === null || _g === void 0 ? void 0 : _g.bold) || ((_h = theme.fonts) === null || _h === void 0 ? void 0 : _h.primary),
                fontWeight: '700'
            },
            lightItalic: {
                fontFamily: ((_j = theme.fonts) === null || _j === void 0 ? void 0 : _j.light_italic) || ((_k = theme.fonts) === null || _k === void 0 ? void 0 : _k.primary),
                fontStyle: 'italic',
                fontWeight: '700'
            },
            italic: {
                fontFamily: ((_l = theme.fonts) === null || _l === void 0 ? void 0 : _l.italic) || ((_m = theme.fonts) === null || _m === void 0 ? void 0 : _m.primary),
                fontStyle: 'italic'
            },
            semiboldItalic: {
                fontFamily: ((_o = theme.fonts) === null || _o === void 0 ? void 0 : _o.semibold_italic) || ((_p = theme.fonts) === null || _p === void 0 ? void 0 : _p.primary),
                fontStyle: 'italic',
                fontWeight: '700'
            },
            boldItalic: {
                fontFamily: ((_q = theme.fonts) === null || _q === void 0 ? void 0 : _q.bold_italic) || ((_r = theme.fonts) === null || _r === void 0 ? void 0 : _r.primary),
                fontStyle: 'italic',
                fontWeight: '700'
            },
            underline: {
                textDecorationLine: 'underline'
            }
        };
    }
})((props) => {
    const [dir] = useDirection();
    const color_scheme = useColorScheme();
    const color_scheme_context = useColorSchemeContext();
    const text = useMemo(() => typeof props.children === 'string'
        ? `text-${props.children}`
        : '', [props.children]);
    const fontWeight = useMemo(() => getFontWeight(props.textStyle, props.children), [props.textStyle, props.children]);
    const isLight = useMemo(() => props.fontWeight === 'light' || fontWeight === 'light', [fontWeight, props.fontWeight]);
    const isSemibold = useMemo(() => props.fontWeight === 'semibold' || fontWeight === 'semibold', [fontWeight, props.fontWeight]);
    const isBold = useMemo(() => props.fontWeight === 'bold' || fontWeight === 'bold', [fontWeight, props.fontWeight]);
    const style = useMemo(() => {
        var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k;
        const styles = [
            (_a = props.style) === null || _a === void 0 ? void 0 : _a.container,
            (_b = props.defaultStyle) === null || _b === void 0 ? void 0 : _b.container,
            props.textStyle,
            props.italic && ((_c = props.style) === null || _c === void 0 ? void 0 : _c.italic),
            isLight && ((_d = props.style) === null || _d === void 0 ? void 0 : _d.light),
            isLight && props.italic && ((_e = props.style) === null || _e === void 0 ? void 0 : _e.lightItalic),
            isSemibold && ((_f = props.style) === null || _f === void 0 ? void 0 : _f.semibold),
            isSemibold && props.italic && ((_g = props.style) === null || _g === void 0 ? void 0 : _g.semiboldItalic),
            isBold && ((_h = props.style) === null || _h === void 0 ? void 0 : _h.bold),
            isBold && props.italic && ((_j = props.style) === null || _j === void 0 ? void 0 : _j.boldItalic),
            props.underline && ((_k = props.style) === null || _k === void 0 ? void 0 : _k.underline),
            Platform.OS === 'android' && { fontWeight: undefined }
        ];
        const text_align = getTextAlign(dir, styles);
        if (text_align) {
            styles.push({ textAlign: text_align });
        }
        if (dir === 'rtl') {
            styles.push({ direction: 'rtl' });
        }
        if (color_scheme_context.scheme !== 'default' && !getTextColor(props.textStyle)) {
            if (color_scheme_context.scheme === 'dark' && props.light === undefined) {
                styles.push({ color: '#fff' });
                return styles;
            }
            if (color_scheme_context.scheme === 'light' && props.light === undefined) {
                styles.push({ color: '#293b51' });
                return styles;
            }
        }
        if (color_scheme.scheme !== 'default' && !getTextColor(props.textStyle)) {
            if (color_scheme.scheme === 'dark' && props.light === false) {
                return styles;
            }
            else {
                styles.push({ color: color_scheme.colors.text });
            }
        }
        if (props.light) {
            styles.push({ color: '#fff' });
        }
        return styles;
    }, [
        props.style,
        props.defaultStyle,
        props.textStyle,
        props.italic,
        isLight,
        isSemibold,
        isBold,
        color_scheme.colors.text,
        color_scheme.colors.default_text,
        color_scheme_context.scheme,
        dir
    ]);
    const handleRef = useCallback((ref) => {
        if (Platform.OS === 'web' && ref && props.title) {
            ref.title = props.title;
        }
    }, []);
    return (_jsx(Animated.Text, Object.assign({}, props, { ref: handleRef, testID: props.testID || text, style: style, children: props.children })));
});
export function createTextView(options) {
    return component(options)((_a) => {
        var { style } = _a, props = __rest(_a, ["style"]);
        return (_jsx(AnimatedTextView, Object.assign({}, props, { defaultStyle: style, textStyle: props.textStyle, children: props.children })));
    });
}
